import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  MenuItem,
  Grid,
  Collapse,
  Alert,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { useSaveCity } from './hooks/useSaveCity';
import { useStates } from '../../hooks/useStates';

export default function CityFormModal({ open, onClose, onSave, city, initialName }) {
  const navigate = useNavigate();
  const { save: saveCity } = useSaveCity();
  const { states } = useStates();
  const [formName, setFormName] = useState('');
  const [formState, setFormState] = useState('');
  const [error, setError] = useState('');

  useEffect(() => {
    if (open) {
      setFormName(city?.name ?? initialName ?? '');
      setFormState(city?.state ?? '');
      setError('');
    }
  }, [open, city, initialName]);

  const isValid = formName.trim() !== '' && formState !== '';

  const save = () => {
    if (!isValid) return;

    const payload = { name: formName.trim(), state: formState };
    saveCity(city ? city.id : null, payload)
      .then((returnFromApi) => {
        onSave(payload, returnFromApi.data);
        onClose();
      })
      .catch((returnFromApi) => {
        if (returnFromApi.response?.status === 401) {
          navigate('/expired-session');
        } else {
          setError(returnFromApi.response?.data ?? 'Erro ao salvar cidade.');
        }
      });
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle sx={{ fontWeight: 'bold' }}>
        {city ? 'Editar Cidade' : 'Nova Cidade'}
      </DialogTitle>
      <DialogContent>
        <Collapse in={!!error} sx={{ mb: 1 }}>
          <Alert severity="error" onClose={() => setError('')}>
            {typeof error === 'string' ? error : 'Erro ao salvar cidade.'}
          </Alert>
        </Collapse>
        <Grid container spacing={2} sx={{ mt: 0 }}>
          <Grid size={{ xs: 12, sm: 8 }}>
            <TextField
              autoFocus
              label="Nome"
              fullWidth
              value={formName}
              onChange={(e) => setFormName(e.target.value)}
              placeholder="Ex: Chapecó"
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 4 }}>
            <TextField
              select
              label="UF"
              fullWidth
              value={formState}
              onChange={(e) => setFormState(e.target.value)}
            >
              {states.map((state) => (
                <MenuItem key={state.value} value={state.value}>
                  {state.value} - {state.description}
                </MenuItem>
              ))}
            </TextField>
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancelar</Button>
        <Button variant="contained" onClick={save} disabled={!isValid}>
          Salvar
        </Button>
      </DialogActions>
    </Dialog>
  );
}

CityFormModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onSave: PropTypes.func.isRequired,
  city: PropTypes.shape({
    id: PropTypes.number,
    name: PropTypes.string,
    state: PropTypes.string,
  }),
  initialName: PropTypes.string,
};

CityFormModal.defaultProps = {
  city: null,
  initialName: '',
};
